import {
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Divider,
  List,
  ListItem,
  Progress,
  HStack,
  Box,

} from '@chakra-ui/react'
import { MdCheckCircle, MdWarning, MdInfo, MdScience, MdStraighten } from 'react-icons/md'

interface RadiologyResultsPanelProps {
  hasResults: boolean
  analyzing: boolean
}

const mockRadiologyResults = {
  title: 'AI Radiology Analysis',
  modality: 'CT Chest',
  findings: [
    { type: 'positive', text: 'Lung fields: Clear, no infiltrates detected', confidence: 96 },
    { type: 'positive', text: 'Heart: Normal size and contour', confidence: 94 },
    { type: 'info', text: 'Mediastinum: Within normal limits', confidence: 92 },
    { type: 'warning', text: 'Small nodule detected in right upper lobe (5mm)', confidence: 88 },
    { type: 'info', text: 'Bones: No acute fractures', confidence: 95 },
  ],
  measurements: [
    { label: 'Nodule diameter', value: '5.2 mm', location: 'Right upper lobe' },
    { label: 'Cardiac ratio', value: '0.48', location: 'Normal range' },
    { label: 'Aortic diameter', value: '32 mm', location: 'Normal' },
  ],
  segmentation: [
    { structure: 'Lungs', volume: '4850 cm³', coverage: '98%' },
    { structure: 'Heart', volume: '620 cm³', coverage: '95%' },
    { structure: 'Vessels', volume: '180 cm³', coverage: '89%' },
  ],
  summary:
    'Overall findings suggest normal chest CT with incidental small pulmonary nodule requiring follow-up. Recommend repeat imaging in 3-6 months per Fleischner criteria.',
}

export default function RadiologyResultsPanel({
  hasResults,
  analyzing,
}: RadiologyResultsPanelProps) {
  if (!hasResults && !analyzing) {
    return (
      <VStack spacing={4} py={8} color="gray.500">
        <MdScience style={{ fontSize: '64px' }} />
        <Text textAlign="center">Upload DICOM files to start AI analysis</Text>
      </VStack>
    )
  }

  if (analyzing) {
    return (
      <VStack spacing={4} py={8}>
        <Progress size="sm" isIndeterminate colorScheme="blue" w="100%" />
        <Text color="gray.600">Processing DICOM series...</Text>
        <Text fontSize="sm" color="gray.500">
          Running segmentation and classification models...
        </Text>
      </VStack>
    )
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'positive':
        return MdCheckCircle
      case 'warning':
        return MdWarning
      default:
        return MdInfo
    }
  }

  const getColor = (type: string) => {
    switch (type) {
      case 'positive':
        return 'green'
      case 'warning':
        return 'orange'
      default:
        return 'blue'
    }
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Analysis Summary */}
      <Card bg="blue.50" borderColor="blue.200" borderWidth={1}>
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Heading size="sm">{mockRadiologyResults.title}</Heading>
            <Badge colorScheme="blue">AI Analyzed</Badge>
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <VStack align="stretch" spacing={2}>
            <HStack>
              <Badge colorScheme="purple">{mockRadiologyResults.modality}</Badge>
            </HStack>
            <Text fontSize="sm">{mockRadiologyResults.summary}</Text>
          </VStack>
        </CardBody>
      </Card>

      {/* Findings */}
      <Card>
        <CardHeader>
          <Heading size="sm">Clinical Findings</Heading>
        </CardHeader>
        <Divider />
        <CardBody>
          <List spacing={4}>
            {mockRadiologyResults.findings.map((finding: any, index: number) => {
              const IconComponent = getIcon(finding.type)
              return (
                <ListItem key={index}>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <IconComponent
                        style={{
                          fontSize: '20px',
                          color: `var(--chakra-colors-${getColor(finding.type)}-500)`,
                        }}
                      />
                      <Text fontSize="sm" flex={1}>
                        {finding.text}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.500">
                        Confidence:
                      </Text>
                      <Progress
                        value={finding.confidence}
                        size="sm"
                        colorScheme={getColor(finding.type)}
                        flex={1}
                        borderRadius="full"
                      />
                      <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                        {finding.confidence}%
                      </Text>
                    </HStack>
                  </VStack>
                </ListItem>
              )
            })}
          </List>
        </CardBody>
      </Card>

      {/* Measurements */}
      <Card>
        <CardHeader>
          <HStack>
            <MdStraighten />
            <Heading size="sm">Measurements</Heading>
          </HStack>
        </CardHeader>
        <Divider />
        <CardBody>
          <VStack align="stretch" spacing={3}>
            {mockRadiologyResults.measurements.map((measurement, index) => (
              <Box key={index} p={3} bg="gray.50" borderRadius="md">
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="semibold">
                    {measurement.label}
                  </Text>
                  <Badge colorScheme="blue">{measurement.value}</Badge>
                </HStack>
                <Text fontSize="xs" color="gray.600">
                  {measurement.location}
                </Text>
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      {/* Segmentation Results */}
      <Card>
        <CardHeader>
          <Heading size="sm">Segmentation Analysis</Heading>
        </CardHeader>
        <Divider />
        <CardBody>
          <VStack align="stretch" spacing={3}>
            {mockRadiologyResults.segmentation.map((seg, index) => (
              <Box key={index}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    {seg.structure}
                  </Text>
                  <HStack spacing={2}>
                    <Badge colorScheme="cyan">{seg.volume}</Badge>
                    <Badge colorScheme="green">{seg.coverage}</Badge>
                  </HStack>
                </HStack>
                <Progress
                  value={parseInt(seg.coverage)}
                  size="sm"
                  colorScheme="green"
                  borderRadius="full"
                />
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      {/* Disclaimer */}
      <Card bg="yellow.50" borderColor="yellow.200" borderWidth={1}>
        <CardBody>
          <HStack spacing={2}>
            <MdWarning
              style={{ fontSize: '20px', color: 'var(--chakra-colors-yellow-600)' }}
            />
            <Text fontSize="xs" color="yellow.800">
              <strong>Disclaimer:</strong> AI results are for reference only. All
              radiological findings must be verified by a qualified radiologist.
            </Text>
          </HStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
